import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationsService } from './configurations.service';
import languages from 'public/data/languages.json';
import { map } from 'rxjs/operators';

@Injectable()
export class LocalesService {
  obs: Observable<any>;

  availableLanguages: any[];
  availableTranslations: any[];
  arrayCodeIso: any[];
  public localeOptionsSubject = new BehaviorSubject({});
  public localeTranslationsSubject = new BehaviorSubject({});
  public localeAvailableByGameSubject = new BehaviorSubject({});

  private defaultLanguages = ['de', 'en', 'es', 'fr'];
  private extendedLanguages = [...this.defaultLanguages, 'pt', 'nl'];

  constructor(
    public http: HttpClient,
    public translate: TranslateService,
    public configurationsService: ConfigurationsService
  ) {
    translate.onLangChange.subscribe(() => {
      if (this.availableLanguages) {
        this.localeOptionsSubject.next({ options: this.getOptions() });
      }
      if (this.availableTranslations) {
        this.localeTranslationsSubject.next({ options: this.getTranslations() });
      }

      if (this.arrayCodeIso) {
        this.localeAvailableByGameSubject.next({
          options: this.getAvailableLocaleOptions(this.arrayCodeIso)
        });
      }
    });
  }

  public getLocales(): Observable<any> {
    if (this.availableLanguages) {
      return of(this.availableLanguages);
    }
    return this.configurationsService.getFile('locales').pipe(
      map((locales) => {
        this.availableLanguages = locales.map((locale: any) => ({
          ...locale,
          name: this.translate.instant(locale.name)
        }));
        this.availableTranslations = languages.map((trans) => {
          const defLang = this.availableLanguages.find((lang) => lang.code === trans);
          return defLang ? defLang : { code: trans };
        });
        this.localeOptionsSubject.next({ options: this.getOptions() });
        return this.availableLanguages;
      })
    );
  }

  getOptionsCollection(collection) {
    return this[collection]
      .map((language) => ({
        key: language.code,
        value: language.name
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }

  getOptions() {
    return this.getOptionsCollection('availableLanguages');
  }

  getExtendedOptions() {
    return this.getOptions().filter((option) => this.extendedLanguages.includes(option.key));
  }

  getDefaultOptions() {
    return this.getOptions().filter((option) => this.defaultLanguages.includes(option.key));
  }

  getOptionsObservable(): Observable<any> {
    return this.localeOptionsSubject.asObservable();
  }

  getTranslations() {
    return this.getOptionsCollection('availableTranslations');
  }

  getTranslationsObservable(): Observable<any> {
    return this.localeTranslationsSubject.asObservable();
  }

  getAvailableLocaleOptions(arrayCodeIso) {
    return this.availableLanguages
      .filter((locale) => arrayCodeIso.indexOf(locale.code) > -1)
      .map((language) => ({
        key: language.code,
        value: language.name
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }

  getAvailableByGameObservable(): Observable<any> {
    this.arrayCodeIso = [];
    return this.localeAvailableByGameSubject.asObservable();
  }

  updateArrayCodeIso(arrayCodeIso) {
    this.arrayCodeIso = arrayCodeIso;
    this.localeAvailableByGameSubject.next({
      options: this.getAvailableLocaleOptions(this.arrayCodeIso)
    });
  }

  getLocale(codeIso) {
    return this.availableLanguages.find((locale) => locale.code === codeIso);
  }

  getLocaleName(codeIso) {
    if (!codeIso) {
      return '';
    }
    return this.getLocale(codeIso)?.name;
  }

  /**
   * Get the language of browser
   * @private
   */
  public getBrowserLocale(): string {
    const locale = localStorage.getItem('backofficelanguage') || 'en';
    if (locale === 'en') {
      return 'en-US';
    }
    return locale;
  }
}
